import { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { IS_PAY_ANYONE_P2P, IS_PAY_ANYONE_REFERRAL } from 'consts/appConsts';
import { PAI_SENDER_NAME, PF_TOKEN } from 'consts/storageConsts';

import { usePayAnyoneTokenQuery } from 'hooks/usePayAnyoneTokenQuery';

import { colors } from 'theme/colors';
import svgs from 'theme/svgs';

import { PAI_AMOUNT_QSP, PAI_BONUS_QSP } from 'utils/queryParamHelpers';
import loggerHelper, { LOG_LEVELS } from 'utils/logger/loggerHelper';
import { setValueInStorage } from 'utils/storage/storageHelper';
import { getValueFromStorage } from 'utils/storage/storageHelper';

const DEFAULT_SENDER_NAME = 'a friend';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
  margin: 0 -23px 24px;

  ${props => css`
    ${props.theme.media.tablet`
        box-shadow: none;
      `};
    ${props.theme.media.desktop`
        box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
        border-radius: 24px 24px 0 0;
        margin: -14px -80px 24px;
      `};
  `};
`;

const DropdownWrapper = styled.button`
  background: none;
  color: inherit;
  border: none;
  padding: 0;
  font: inherit;
  cursor: pointer;
  outline: inherit;

  display: flex;
  justify-content: space-between;
  height: 24px;
  margin: 8px 16px 16px;
  align-items: center;
`;

const CopyWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const MoneyImage = styled.img`
  width: 24px;
  height: 24px;
  margin: 0 6px;
`;

const BoldText = styled.span`
  font-weight: 600 !important;
`;

const Text = styled.span`
  font-size: 16px;
`;

const ProgressBarWrapper = styled.div`
  display: flex;
  height: 4px;
  margin: 8px 16px;
`;

const ProgressBarSection = styled.div`
  border-radius: 4px;
  margin: 0px 2px;
  background-color: ${colors.lightGrey};
  width: 33.333333%;
  ${props =>
    props.isComplete &&
    css`
      background-color: ${colors.util.logoBrandColor};
    `};
`;

const DropdownArrow = styled.img`
  height: 8px;
  ${props =>
    props.dropdownOpen
      ? css`
          transform: rotateX(0.5turn);
        `
      : css`
          transform: rotateX(0);
        `}
  transition: all 0.3s ease-in-out;
`;

const DropdownSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;

  ${props =>
    props.dropdownOpen
      ? css`
          border-top: 1px solid ${colors.lightGrey};
          max-height: 140px;
          opacity: 1;
        `
      : css`
          max-height: 0;
          line-height: 0;
          opacity: 0;
        `}

  transition: max-height 0.3s ease-in-out,
    opacity 0.3s ease-in-out;
`;

const DropdownSectionPadding = styled.div`
  padding: 16px 24px;
`;

const RowWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  flex: 1;
  padding: 0 0 16px;
`;

const TotalRowWrapper = styled(RowWrapper)`
  border-top: 1px solid ${colors.lightGrey};
  font-weight: 600 !important;
  padding: 16px 0 0;
`;

const DropdownDefaultText = styled.p`
  font-size: 14px;
  line-height: 17px;
  margin: 0;
`;

export default function ProgressBar({ currentPageIndex, totalPagesInFlow }) {
  const [senderName, setSenderName] = useState(
    getValueFromStorage(PAI_SENDER_NAME)
  );
  const sentAmount = Number(PAI_AMOUNT_QSP).toFixed(2).replace('.00', '');
  const bonusAmount = Number(PAI_BONUS_QSP).toFixed(2).replace('.00', '');
  const totalAmount = (Number(sentAmount) + Number(bonusAmount))
    .toFixed(2)
    .replace('.00', '');

  const showBonusDropdownToggle = !!PAI_BONUS_QSP && !!senderName;
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const toggleDropdown = () => {
    if (showBonusDropdownToggle) {
      setDropdownOpen(!dropdownOpen);
    }
  };

  const renderProgressBarSections = () =>
    new Array(totalPagesInFlow)
      .fill(null)
      .map((_el, i) => (
        <ProgressBarSection key={i} isComplete={i < currentPageIndex} />
      ));

  const pfToken = getValueFromStorage(PF_TOKEN);
  const { data, error } = usePayAnyoneTokenQuery(pfToken, {
    skip: !pfToken || !IS_PAY_ANYONE_P2P || IS_PAY_ANYONE_REFERRAL,
  });

  // Fetch sender name for P2P flow
  useEffect(() => {
    const isFetchOrStepUpError = [
      'PayAnyoneFetchError',
      'StepUpRequiredError',
    ].includes(data?.unauthenticated?.p2p?.pay_anyone?.__typename);
    if (error || isFetchOrStepUpError) {
      loggerHelper({
        level: LOG_LEVELS.ERROR,
        message: 'Could not fetch sender info for P2P flow.',
        info: error ?? data?.unauthenticated?.p2p?.pay_anyone,
      });
      setSenderName(DEFAULT_SENDER_NAME);
      return;
    }
    if (data) {
      const { name } = data?.unauthenticated?.p2p?.pay_anyone?.sender;
      const senderName = name ?? DEFAULT_SENDER_NAME;
      setSenderName(senderName);
      setValueInStorage(PAI_SENDER_NAME, senderName);
    }
  }, [data, error]);

  return (
    <Wrapper>
      <ProgressBarWrapper>{renderProgressBarSections()}</ProgressBarWrapper>
      <DropdownWrapper onClick={toggleDropdown}>
        <CopyWrapper>
          <MoneyImage alt="Money Stack Icon" src={svgs.moneyStack}></MoneyImage>
          <BoldText>{`$${totalAmount}`}&nbsp;</BoldText>
          {IS_PAY_ANYONE_REFERRAL ? (
            <Text>{`bonus waiting for you`}</Text>
          ) : (
            <Text>{`coming your way`}</Text>
          )}
        </CopyWrapper>
        {showBonusDropdownToggle && (
          <DropdownArrow
            alt="Dropdown Arrow"
            dropdownOpen={dropdownOpen}
            src={svgs.downArrowGrey}
          />
        )}
      </DropdownWrapper>

      {showBonusDropdownToggle && (
        <DropdownSectionWrapper dropdownOpen={dropdownOpen}>
          <DropdownSectionPadding>
            <RowWrapper>
              <DropdownDefaultText>{`From ${senderName}`}</DropdownDefaultText>
              <DropdownDefaultText>{`$${sentAmount}`}</DropdownDefaultText>
            </RowWrapper>
            <RowWrapper>
              <DropdownDefaultText>Bonus from Chime</DropdownDefaultText>
              <DropdownDefaultText>{`$${bonusAmount}`}</DropdownDefaultText>
            </RowWrapper>
            <TotalRowWrapper>
              <DropdownDefaultText>Total</DropdownDefaultText>
              <DropdownDefaultText>{`$${totalAmount}`}</DropdownDefaultText>
            </TotalRowWrapper>
          </DropdownSectionPadding>
        </DropdownSectionWrapper>
      )}
    </Wrapper>
  );
}

ProgressBar.propTypes = {
  amount: PropTypes.string,
  senderName: PropTypes.string,
  currentPageIndex: PropTypes.number,
  totalPagesInFlow: PropTypes.number,
};
